import "./tablePage.scss";
import {Button, Card, Typography} from 'antd';
import React, {useContext, useEffect, useMemo, useState} from "react";
import ModalBase from "../../shared/components/modal/ModalBase";
import ScrollToTop from "../../shared/components/common/ScrollToTop";
import {internalApi} from "../../common/axios";
import {handleResponse} from "../../shared/utils/net";
import toast from "react-hot-toast";
import {NewDatatable} from "../../shared/components/datatable/NewDatatable";
import CsvUploadModal from "../csv/CsvUploadModal";
import {buildDataCsv, downloadCsv} from "../csv/csvTemplate";
import dayjs from "dayjs";
import {DarkModeContext} from "../../shared/contexts/darkModeContext";
import {DownloadOutlined, UploadOutlined} from "@ant-design/icons";
import ServerRefresh from "../../shared/components/common/ServerRefresh";

const {Title, Text} = Typography;

const TablePage = (props) => {
    const {darkMode} = useContext(DarkModeContext);
    const [modalOpen, setModalOpen] = useState(false);
    const [csvUploadModalOpen, setCsvUploadModalOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: props.pageSize || 50,
            defaultCurrent: 1,
            defaultPageSize: 50,
            pageSizeOptions: ['20', '50', '100', '500'],
            position: ['bottomCenter'],
            showSizeChanger: true,
            total: 0,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            showQuickJumper: true,
        },
        filters: props.filters,
    });
    const fetchData = async () => {
        setLoading(true);
        if (props.url) {
            const paramPrefix = props.url.includes('?') ? '&' : '?';
            let url = `${props.url}`;
            url += `${paramPrefix}page=${tableParams.pagination.current}`;
            url += `&limit=${tableParams.pagination.pageSize}`;
            url += `&filters=${JSON.stringify(tableParams.filters || {})}`;
            url += `&field=${tableParams.field || ''}`;
            url += `&order=${tableParams.order || ''}`;

            const {pid} = tableParams?.filters || {};
            if (props.searchParam && pid) {
                props.searchParam.pid = pid;
            }

            try {
                const resp = await internalApi.get(url);
                handleResponse(resp,
                    (data) => {
                        const resData = data.rows.map((row, idx) => ({...row, key: idx}));
                        props.setTblData && props.setTblData(data.tbl);
                        props.setRowData && props.setRowData(resData[0]);
                        props.setRows && props.setRows(resData);
                        setRows(resData);
                        setTableParams((prev) => ({
                            ...prev,
                            pagination: {
                                ...prev.pagination,
                                total: data.total
                            }
                        }));
                        setLoading(false);
                    },
                    (msg) => {
                        toast.error(`${msg}`);
                        setLoading(false);
                    });
            } catch (ex) {
                setLoading(false);
            }
        } else {
            const localData = props.localData?.map((row, idx) => ({...row, key: idx})) || [];
            setRows(localData);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [props.url, props.setRows, tableParams.pagination.current, tableParams.filters, props.localData, props.isChanged]);

    useEffect(() => {
        if (props.refreshRef) {
            props.refreshRef.current = fetchData;
        }
    }, [props.refreshRef]);

    const handleModalOpen = (record) => {
        if (props.setRowData) {
            props.setRowData(record);
            setModalOpen(true);
        }
    }

    // CSV 모달은 props.rowData 가 아니라 조회된 rows 를 받으므로 setRowData 여부와 상관없이 연다.
    const handleCsvUploadModalOpen = () => {
        setCsvUploadModalOpen(true);
    }

    const handleClose = () => {
        setModalOpen(false);
        setCsvUploadModalOpen(false);
        fetchData();
    }

    const cardStyle = useMemo(() => ({
        background: darkMode ? 'rgba(30, 30, 30, 0.6)' : 'rgba(255, 255, 255, 0.8)',
        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
        borderRadius: 16,
        backdropFilter: 'blur(20px)',
        boxShadow: darkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.08)',
        padding: '24px'
    }), [darkMode]);

    return (
        <div className="enhanced-table-page">
            <Card
                style={cardStyle}
                className="table-card"
            >
                {props.buttonText && (
                    <Button
                        type={"primary"}
                        onClick={handleModalOpen}
                        style={{margin: 10}}
                    >
                        {props.buttonText}
                    </Button>
                )}
                {props.extraButtons}
                {props.csvUpload && (
                    <Button
                        type="default"
                        icon={<UploadOutlined/>}
                        onClick={handleCsvUploadModalOpen}
                        style={{
                            borderRadius: 8,
                            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
                            background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                            fontWeight: 500,
                            marginLeft: '10px',
                        }}
                    >
                        CSV Upload
                    </Button>
                )}

                {/* 현재 페이지에 표시된 행을 업로드 양식 그대로 내려받는다 (수정 후 재업로드 가능) */}
                {props.csvColumns?.length > 0 && props.csvPageDownload !== false && (
                    <Button
                        type="default"
                        icon={<DownloadOutlined/>}
                        disabled={rows.length === 0}
                        onClick={() => downloadCsv(
                            buildDataCsv(props.csvColumns, rows),
                            `${(props.csvTemplateName || 'data.csv').replace(/(_form)?\.csv$/, '')}_${dayjs().format('YYYYMMDDHHmmss')}.csv`
                        )}
                        style={{
                            borderRadius: 8,
                            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
                            background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                            fontWeight: 500,
                            marginLeft: '10px',
                        }}
                    >
                        현재 페이지 CSV 다운로드
                    </Button>
                )}

                {props.csvDownload && (
                    <Button
                        type="default"
                        icon={<DownloadOutlined/>}
                        onClick={() => {
                            const url = props.csvDownload;
                            if (url) {
                                window.open(url, '_blank');
                            } else {
                                toast.error('CSV 다운로드 URL이 설정되지 않았습니다.');
                            }
                        }}
                        style={{
                            borderRadius: 8,
                            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
                            background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                            fontWeight: 500,
                            marginLeft: '10px',
                        }}
                    >
                        CSV Download
                    </Button>
                )}

                {props.refreshTypeFunc && (
                    <>
                        <ServerRefresh refreshTypeFunc={props.refreshTypeFunc}
                                       reloadTimeRefetch={props.reloadTimeRefetch}/>
                        <Text type="secondary" style={{fontSize: '14px', marginTop: 4}}>
                            마지막 동기화 (이벤트, 상점정보 등) : {props.reloadTime ? props.reloadTime + ' (KST)' : '정보 없음'}
                        </Text>
                    </>


                )}

                {props.columns && (
                    <NewDatatable
                        columns={props.columns}
                        customRowClick={props?.handleCustomRowClick || handleModalOpen}
                        rows={rows}
                        expandedRowRender={props?.expandedRowRender}
                        tableParams={tableParams}
                        setTableParams={setTableParams}
                        loading={loading}
                        rowSelection={props?.rowSelection}
                        title={props.tableTitle}
                    />
                )}

                {/* 데이터가 없을 때 */}
                {!loading && rows.length === 0 && (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.45)'
                    }}>
                        <div style={{fontSize: '48px', marginBottom: 16}}>📊</div>
                        <Title level={4} type="secondary">No Data Available</Title>
                        <Text type="secondary">
                            {props.emptyMessage || 'There are no items to display at the moment.'}
                        </Text>
                    </div>
                )}
            </Card>

            {/* 모달들 */}
            {props.modal && (
                <ModalBase
                    open={modalOpen}
                    handleClose={handleClose}
                    rowData={props.rowData}
                    ModalPage={props.modal}
                    tblData={props.tblData}
                    modalOptions={props.modalOptions}
                    width={props.modalWidth}
                    resizable={props.modalResizable}
                />
            )}

            {props.csvUpload && (
                <ModalBase
                    open={csvUploadModalOpen}
                    handleClose={handleClose}
                    rowData={rows}
                    ModalPage={CsvUploadModal}
                    tblData={props.tblData}
                    url={props.csvUpload}
                    entityModel={props.entityModel}
                    identityKey={props.csvIdentityKey}
                    deleteMissing={props.csvDeleteMissing !== false}
                    compareUrl={props.csvCompareUrl}
                    guide={props.csvGuide}
                    csvColumns={props.csvColumns}
                    templateName={props.csvTemplateName}
                    checkRow={props.csvCheckRow}
                    width={props.csvModalWidth || 1400}
                    resizable
                />
            )}

            <ScrollToTop/>

        </div>
    );
};

export default TablePage;